import { Slideshow } from '@archetype-themes/scripts/modules/slideshow'

const slideshowInstances = new WeakMap()
const bgSlideshowInstances = new WeakMap()
const bgAutoplayTimers = new WeakMap()

function prepareBgVideo(video) {
  video.muted = true
  video.defaultMuted = true
  video.autoplay = true
  video.setAttribute('muted', '')
  video.setAttribute('autoplay', '')
  video.setAttribute('playsinline', '')
  video.setAttribute('webkit-playsinline', '')
  video.loop = true
}

function isMobileViewport() {
  return window.matchMedia('(max-width: 768px)').matches
}

function getActiveBgVideos(container) {
  const hasSplitVideos = container.querySelector('.grid-promo-tile__bg-media--video-desktop')

  if (!hasSplitVideos) {
    return [...container.querySelectorAll('.grid-promo-tile__bg-media--video video')]
  }

  const wrapperSelector = isMobileViewport()
    ? '.grid-promo-tile__bg-media--video-mobile'
    : '.grid-promo-tile__bg-media--video-desktop'
  const wrapper = container.querySelector(wrapperSelector)

  return wrapper ? [...wrapper.querySelectorAll('video')] : []
}

function syncBgVideosInContainer(container, shouldPlay) {
  const activeVideos = shouldPlay ? getActiveBgVideos(container) : []
  const allVideos = container.querySelectorAll('video')

  allVideos.forEach((video) => {
    prepareBgVideo(video)

    if (activeVideos.includes(video)) {
      const playPromise = video.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    } else {
      video.pause()
    }
  })
}

function syncBgSlideVideos(bgRoot, activeIndex) {
  const slides = bgRoot.querySelectorAll('.grid-promo-tile__bg-slide')

  if (!slides.length) {
    syncBgVideosInContainer(bgRoot, true)
    return
  }

  slides.forEach((slide, index) => {
    syncBgVideosInContainer(slide, activeIndex === null || index === activeIndex)
  })
}

function getBgSlideshowActiveIndex(bgEl) {
  const instance = bgSlideshowInstances.get(bgEl)
  if (!instance || !instance.slideshow) return 0
  return instance.slideshow.selectedIndex || 0
}

function resolvePromoCtaLink(tile, activeIndex) {
  const fallback = tile.dataset.ctaFallback || ''
  const bgSlideshow = tile.querySelector('[data-promo-bg-flickity]')

  if (bgSlideshow) {
    const slides = bgSlideshow.querySelectorAll('.grid-promo-tile__bg-slide')
    const slide = slides[activeIndex]
    return slide?.dataset.bgLink || fallback
  }

  const bgRoot = tile.querySelector('.grid-promo-tile__bg')
  const singleSlide = bgRoot?.querySelector('.grid-promo-tile__bg-slide')
  if (singleSlide?.dataset.bgLink) return singleSlide.dataset.bgLink

  const singleLink = bgRoot?.querySelector('.grid-promo-tile__bg-link')
  if (singleLink?.href) return singleLink.getAttribute('href')

  return fallback
}

function syncPromoCta(tile, activeIndex) {
  const cta = tile.querySelector('[data-promo-cta]')
  if (!cta) return

  const link = resolvePromoCtaLink(tile, activeIndex ?? 0)
  if (link) {
    cta.href = link
  }
}

function syncBgSlideState(tile, bgRoot, activeIndex) {
  syncBgSlideVideos(bgRoot, activeIndex)
  syncPromoCta(tile, activeIndex)
}

function destroyOrphanedFlickity(el) {
  if (!el || !window.Flickity) return

  const flickity = window.Flickity.data(el)
  if (flickity && typeof flickity.destroy === 'function') {
    flickity.destroy()
  }
}

function stopBgAutoplay(el) {
  const timer = bgAutoplayTimers.get(el)
  if (!timer) return

  clearInterval(timer)
  bgAutoplayTimers.delete(el)
}

function startBgAutoplay(el, slideshowInstance, seconds) {
  stopBgAutoplay(el)

  const autoplaySeconds = parseInt(seconds, 10) || 0
  if (autoplaySeconds <= 0 || !slideshowInstance?.slideshow) return

  const flickity = slideshowInstance.slideshow
  const timer = setInterval(() => {
    if (!el.isConnected) {
      stopBgAutoplay(el)
      return
    }

    flickity.next(true)
  }, autoplaySeconds * 1000)

  bgAutoplayTimers.set(el, timer)
}

function resyncAllPromoBgVideos() {
  document.querySelectorAll('[data-grid-promo-tile]').forEach((tile) => {
    const bgEl = tile.querySelector('[data-promo-bg-flickity]') || tile.querySelector('.grid-promo-tile__bg')
    if (!bgEl) return

    const activeIndex = bgEl.hasAttribute('data-promo-bg-flickity')
      ? getBgSlideshowActiveIndex(bgEl)
      : null

    syncBgSlideState(tile, bgEl, activeIndex)
    refreshBgSlideshow(tile)
  })
}

let resizeTimer
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer)
  resizeTimer = setTimeout(resyncAllPromoBgVideos, 150)
})

function destroySlideshow(el) {
  const instance = slideshowInstances.get(el)
  if (instance && typeof instance.destroy === 'function') {
    instance.destroy()
    slideshowInstances.delete(el)
  }
  el.classList.remove('is-ready')
  el.style.removeProperty('--promo-slide-height')
  el.style.height = ''
}

function lockSlideHeight(el) {
  const firstSlide = el.querySelector('.grid-promo-tile__slide')
  if (!firstSlide) return

  const height = firstSlide.offsetHeight
  if (height > 0) {
    el.style.setProperty('--promo-slide-height', `${height}px`)
  }
}

function watchSlideImages(el) {
  el.querySelectorAll('.grid-promo-tile__slide img').forEach((img) => {
    if (img.complete) return
    img.addEventListener('load', () => lockSlideHeight(el), { once: true })
  })
}

function refreshBgSlideshow(tile) {
  const el = tile.querySelector('[data-promo-bg-flickity]')
  if (!el) return

  const instance = bgSlideshowInstances.get(el)
  if (!instance) return

  if (typeof instance.resize === 'function') {
    instance.resize()
  }

  if (instance.slideshow) {
    instance.slideshow.resize()
  }

  startBgAutoplay(el, instance, tile.dataset.bgAutoplay)
}

function observePromoTileVisibility(tile) {
  if (tile.dataset.promoVisibilityObserved === 'true') return
  tile.dataset.promoVisibilityObserved = 'true'

  const bgEl = tile.querySelector('[data-promo-bg-flickity]')
  if (!bgEl) return

  if (!('IntersectionObserver' in window)) {
    const instance = bgSlideshowInstances.get(bgEl)
    startBgAutoplay(bgEl, instance, tile.dataset.bgAutoplay)
    return
  }

  let isVisible = false

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = tile.querySelector('[data-promo-bg-flickity]')
        if (!el) return

        if (entry.isIntersecting) {
          if (!isVisible) {
            isVisible = true
            refreshBgSlideshow(tile)
          }
          return
        }

        if (isVisible) {
          isVisible = false
          stopBgAutoplay(el)
        }
      })
    },
    { threshold: 0.15 }
  )

  observer.observe(tile)
}

function destroyBgSlideshow(el) {
  stopBgAutoplay(el)

  const instance = bgSlideshowInstances.get(el)
  if (instance && typeof instance.destroy === 'function') {
    instance.destroy()
    bgSlideshowInstances.delete(el)
  } else {
    destroyOrphanedFlickity(el)
  }

  el.classList.remove('is-ready')
}

function initPromoBgSlideshow(tile) {
  const el = tile.querySelector('[data-promo-bg-flickity]')
  if (!el) return

  const bgSlideCount = parseInt(tile.dataset.bgSlideCount, 10) || 0
  if (bgSlideCount < 2) return

  destroyBgSlideshow(el)

  const slideshow = new Slideshow(el, {
    fade: true,
    wrapAround: true,
    pageDots: false,
    prevNextButtons: false,
    adaptiveHeight: false,
    setGallerySize: false,
    draggable: false,
    contain: true,
    avoidReflow: false,
    // Theme Slideshow throws when autoPlay is set outside `.slideshow-wrapper`.
    autoPlay: false,
    pauseAutoPlayOnHover: false,
    callbacks: {
      onInit: () => {
        el.classList.add('is-ready')
        syncBgSlideState(tile, el, 0)
        requestAnimationFrame(() => {
          refreshBgSlideshow(tile)
        })
      },
      onChange: (index) => {
        syncBgSlideState(tile, el, index)
      }
    }
  })

  bgSlideshowInstances.set(el, slideshow)
  observePromoTileVisibility(tile)
}

function initPromoSlideshow(tile) {
  const el = tile.querySelector('[data-promo-flickity]')
  if (!el) return

  const slideCount = parseInt(tile.dataset.slideCount, 10) || 0
  if (slideCount < 2) return

  destroySlideshow(el)
  lockSlideHeight(el)
  watchSlideImages(el)

  const autoplaySeconds = parseInt(tile.dataset.autoplay, 10) || 0
  const slideshow = new Slideshow(el, {
    cellAlign: 'left',
    wrapAround: true,
    pageDots: false,
    prevNextButtons: false,
    adaptiveHeight: false,
    setGallerySize: false,
    dragThreshold: 25,
    contain: true,
    percentPosition: true,
    avoidReflow: true,
    autoPlay: autoplaySeconds > 0 ? autoplaySeconds * 1000 : false,
    pauseAutoPlayOnHover: true,
    callbacks: {
      onInit: () => {
        el.style.height = ''
        lockSlideHeight(el)
        el.classList.add('is-ready')
      }
    }
  })

  slideshowInstances.set(el, slideshow)
}

function initPromoTile(tile) {
  try {
    const bgEl = tile.querySelector('[data-promo-bg-flickity]') || tile.querySelector('.grid-promo-tile__bg')
    if (bgEl) {
      const activeIndex = bgEl.hasAttribute('data-promo-bg-flickity') ? 0 : null
      syncBgSlideState(tile, bgEl, activeIndex)
    }

    initPromoBgSlideshow(tile)
    initPromoSlideshow(tile)
    observePromoTileVisibility(tile)

    tile.querySelectorAll('.grid-promo-tile__bg-slide img').forEach((img) => {
      if (img.complete) return
      img.addEventListener('load', () => refreshBgSlideshow(tile), { once: true })
    })
  } catch (error) {
    console.error('Grid promo tile init failed:', error)
  }
}

let initTimer
function scheduleInitAllPromoTiles(root) {
  clearTimeout(initTimer)
  initTimer = setTimeout(() => initAllPromoTiles(root), 50)
}

function initAllPromoTiles(root) {
  const scope = root || document
  scope.querySelectorAll('[data-grid-promo-tile]').forEach(initPromoTile)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleInitAllPromoTiles())
} else {
  scheduleInitAllPromoTiles()
}

document.addEventListener('collection-component:loaded', () => {
  scheduleInitAllPromoTiles()
})

document.addEventListener('collection:reloaded', () => {
  scheduleInitAllPromoTiles()
})

window.addEventListener('load', () => {
  scheduleInitAllPromoTiles()
})
